import { Injectable, Inject, Injector } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router, NavigationEnd } from '@angular/router';
import * as semver from 'semver';
import { retry, map, switchMap, filter, exhaustMap, catchError, tap, mergeMap, takeUntil, take } from 'rxjs/operators';
import { Observable, timer, Subject, EMPTY, of } from 'rxjs';
import { WINDOW } from './window.provider';
import { MatSnackBar, MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { VERSION } from '@env/version';
import * as moment from 'moment';
import { discardPersistedAppState$ } from '@app/core/functions/storage-helpers';
import { GUARDED_LOGOUT } from './guarded-logout.tokens';
import { IVersion, IVersionCheck, IVersionUpdateEvent, UpdateTrigger } from './version.models';

// A client this many minor versions behind the served build is treated like a major update.
export const FORCED_UPDATE_MINOR_GAP = 3;
export const FORCED_UPDATE_LOOP_GUARD_MS = 30 * 60 * 1000;
export const FORCED_UPDATE_ATTEMPT_KEY = 'hbc.forcedUpdateAttempt';
const LOGIN_URL = '/auth/login';

export function classifyVersion(current: IVersion, server: IVersion): IVersionCheck {
  const valid = !!semver.valid(current?.version) && !!semver.valid(server?.version);
  const comparison = valid ? semver.diff(current.version, server.version) : null;
  const check: IVersionCheck = { version: server, current, comparison, updateRecommendation: 'none' };
  if (!comparison || semver.lte(server.version, current.version)) {
    return check;
  }
  if (comparison === 'major' || comparison === 'premajor') {
    return { ...check, updateRecommendation: 'required', reason: 'major' };
  }
  if (semver.minor(server.version) - semver.minor(current.version) >= FORCED_UPDATE_MINOR_GAP) {
    return { ...check, updateRecommendation: 'required', reason: 'minor-gap' };
  }
  return { ...check, updateRecommendation: 'recommended' };
}

@Injectable({
  providedIn: 'root',
})
export class VersionService {
  versionSnackbar: MatSnackBarRef<SimpleSnackBar>;
  versionFile = 'assets/config/version.json';
  currentVersion: IVersion = VERSION;
  private readonly updateEvents = new Subject<IVersionUpdateEvent>();
  // Telemetry is emitted here and tracked by ApplicationInsightsService, which depends on this service.
  readonly updateEvents$ = this.updateEvents.asObservable();
  private readonly updateStarted = new Subject<void>();

  get serverVersion(): Observable<IVersionCheck> {
    const headers = new HttpHeaders({
      'Cache-Control': 'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
      Pragma: 'no-cache',
      Expires: '0',
    });

    return this.http.get<IVersion>(this.versionFile, { headers }).pipe(
      retry(2),
      map(serverVer => classifyVersion(this.currentVersion, serverVer))
    );
  }

  private get loginOpened$(): Observable<NavigationEnd> {
    return this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      filter(event => event.urlAfterRedirects.startsWith(LOGIN_URL))
    );
  }

  constructor(
    private http: HttpClient,
    @Inject(WINDOW)
    private window: Window,
    private snackbar: MatSnackBar,
    private dialog: MatDialog,
    private router: Router,
    private injector: Injector
  ) {
    this.setupTimer();
    this.setupLoginRefresh();
  }

  setupTimer(initialCheck = 60 * 1000, subsequentChecks = 10 * 60 * 1000) {
    timer(initialCheck, subsequentChecks)
      .pipe(
        filter(() => this.isWithinCheckWindow()),
        switchMap(() => this.serverVersion.pipe(catchError(() => EMPTY))),
        filter(ver => ver.updateRecommendation !== 'none'),
        mergeMap(ver => this.notifyUpdate$(ver, subsequentChecks)),
        untilDestroyed(this, 'destroy')
      )
      .subscribe();
  }

  destroy() {}

  // Nothing unsaved is at risk on the login page (a session expiry lands here too), so a required update reloads.
  private setupLoginRefresh() {
    this.loginOpened$
      .pipe(
        // Once an update has started the page is about to reload, so the login-page check must not fire again.
        takeUntil(this.updateStarted),
        exhaustMap(() => this.serverVersion.pipe(catchError(() => EMPTY))),
        filter(ver => ver.updateRecommendation === 'required'),
        exhaustMap(ver => this.refreshOnLogin$(ver)),
        untilDestroyed(this, 'destroy')
      )
      .subscribe();
  }

  private isWithinCheckWindow(): boolean {
    // check version only during working hours, can be improved more by pausing whole long running user session
    const currentHour = moment().hour();
    return currentHour >= 8 && currentHour <= 22;
  }

  private notifyUpdate$(ver: IVersionCheck, snackbarDuration: number): Observable<unknown> {
    const required = ver.updateRecommendation === 'required';
    const versionDate = moment(ver.version.date).format('LL');
    this.versionSnackbar = this.snackbar.open(
      `New version available ${ver.version.version} (${versionDate})`,
      required ? 'Logout & Update' : 'Update',
      {
        duration: snackbarDuration, // match the check timer, so essentially it'll remain open and naggy
        politeness: 'assertive',
        horizontalPosition: 'left',
      }
    );
    const accepted$ = this.versionSnackbar.onAction();
    return required ? accepted$.pipe(switchMap(() => this.logoutAndUpdate$(ver))) : accepted$.pipe(tap(() => this.reload()));
  }

  // Resolved on click: the logout's AudioRecorderService depends back on this service via ApplicationInsightsService.
  private logoutAndUpdate$(ver: IVersionCheck): Observable<unknown> {
    return this.injector
      .get(GUARDED_LOGOUT)
      .guardedLogout$(this.dialog)
      .pipe(
        tap(() => {
          this.rememberAttempt(ver.version.version);
          this.startUpdate(ver, 'user');
        }),
        switchMap(() => this.loginOpen$()),
        switchMap(() => this.reloadWithoutSessionState$())
      );
  }

  // Accepted on the login page itself, navigating there again ends no navigation, so do not wait for one.
  private loginOpen$(): Observable<unknown> {
    return this.router.url.startsWith(LOGIN_URL) ? of(true) : this.loginOpened$.pipe(take(1));
  }

  private refreshOnLogin$(ver: IVersionCheck): Observable<unknown> {
    if (this.recentlyAttempted(ver.version.version)) {
      // The previous reload did not move this client forward - let the user log in instead of looping.
      this.emit('forced-upgrade-skipped', ver, 'recent-attempt');
      return EMPTY;
    }
    if (!this.rememberAttempt(ver.version.version)) {
      // Without the marker the loop guard cannot stop the next login page from reloading again.
      this.emit('forced-upgrade-skipped', ver, 'storage-unavailable');
      return EMPTY;
    }
    this.startUpdate(ver, 'login');
    return this.reloadWithoutSessionState$();
  }

  private startUpdate(ver: IVersionCheck, trigger: UpdateTrigger) {
    this.updateStarted.next();
    this.emit('forced-upgrade', ver, ver.reason, trigger);
  }

  // The logout reset every store, but throttled persistence may still hold the previous session's state.
  private reloadWithoutSessionState$(): Observable<unknown> {
    return discardPersistedAppState$().pipe(tap(() => this.reload()));
  }

  private reload() {
    // disable warning, but we need this!
    // tslint:disable-next-line: deprecation
    this.window.location.reload(true);
  }

  private emit(kind: IVersionUpdateEvent['kind'], ver: IVersionCheck, reason: string, trigger?: UpdateTrigger) {
    this.updateEvents.next({ kind, fromVersion: ver.current.version, toVersion: ver.version.version, reason, trigger });
  }

  private recentlyAttempted(toVersion: string): boolean {
    try {
      const attempt = JSON.parse(this.window.localStorage.getItem(FORCED_UPDATE_ATTEMPT_KEY));
      return attempt?.to === toVersion && Date.now() - attempt.at < FORCED_UPDATE_LOOP_GUARD_MS;
    } catch (e) {
      return false;
    }
  }

  private rememberAttempt(toVersion: string): boolean {
    try {
      this.window.localStorage.setItem(FORCED_UPDATE_ATTEMPT_KEY, JSON.stringify({ to: toVersion, at: Date.now() }));
      return true;
    } catch (e) {
      return false;
    }
  }
}
